import React, { useEffect, useState } from "react";
import PostalCodeAsyncTextInput from "../PostalCodeAsyncTextInput/PostalCodeAsyncTextInput";
import TextInput from "../TextInput/TextInput";
import usePostalCode from "../../../../hooks/usePostalCode";
import { Row, Col, Select, Form } from "antd";

const { Option } = Select;

export default function PostalCodeFieldsBlock({
  initialFormValues,
  form,
  externalLoadingSetter,
  loadingSetter,
  loadingState,
  editMode,
}) {
  const { setFieldsValue, setFields, getFieldDecorator } = form;
  const { getPostalCodeInfo } = usePostalCode();

  const [internalLoading, setInternalLoading] = useState(false);
  const isLoading =
    typeof loadingState === "boolean" ? loadingState : internalLoading;

  const [cityChoices, setCityChoices] = useState([]);
  const [hasMultipleCities, setHasMultipleCities] = useState(false);

  const setLoading = (v) => {
    if (typeof loadingSetter === "function") loadingSetter(v);
    else if (typeof externalLoadingSetter === "function")
      externalLoadingSetter(v);
    else setInternalLoading(v);
  };

  const resetCityState = () => {
    setCityChoices([]);
    setHasMultipleCities(false);
  };

  const handlePostCode = async (postCode) => {
    if (!postCode) {
      resetCityState();
      setFieldsValue({
        city: undefined,
        country: undefined,
        cityCode: undefined,
      });
      return false;
    }

    try {
      setLoading(true);

      const results = await getPostalCodeInfo(postCode);
      const list = Array.isArray(results) ? results : results ? [results] : [];

      if (!list.length) {
        resetCityState();
        setFields({
          postalCode: {
            value: postCode,
            errors: [new Error("El código postal no existe.")],
          },
          city: { value: undefined },
          cityCode: { value: undefined },
          country: { value: undefined },
        });
        setLoading(false);
        return false;
      }

      if (list.length === 1) {
        resetCityState();
        const { city, cityCode, country } = list[0];
        setFieldsValue({ city, country, cityCode });
        setLoading(false);
        return true;
      }

      setCityChoices(
        list.map(({ city, cityCode, country }) => ({ city, cityCode, country }))
      );
      setHasMultipleCities(true);
      setFieldsValue({
        city: undefined,
        country: undefined,
        cityCode: undefined,
      });
      setLoading(false);
      return true;
    } catch (error) {
      resetCityState();
      setFields({
        postalCode: {
          value: postCode,
          errors: [new Error("Error al obtener el código postal.")],
        },
        city: { value: null },
        cityCode: { value: null },
        country: { value: null },
      });
      setLoading(false);
      return false;
    }
  };

  const handleCitySelect = (cityCodeSelected) => {
    const selected = cityChoices.find((c) => c.cityCode === cityCodeSelected);
    if (!selected) return;
    const { city, cityCode, country } = selected;
    setFieldsValue({ city, cityCode, country });
  };

  useEffect(() => {
    if (!initialFormValues?.postalCode) {
      resetCityState();
    }
  }, [initialFormValues?.postalCode]);

  return (
    <Row className="PostalCodeFieldsBlock" type="flex" justify="center">
      <Col span={7}>
        <PostalCodeAsyncTextInput
          label="Código postal"
          name="postalCode"
          getFieldDecorator={getFieldDecorator}
          initialValue={initialFormValues?.postalCode}
          disabled={initialFormValues?.postalCode ? true : false}
          required
          requiredMessage="Campo obligatorio"
          callback={handlePostCode}
          loading={isLoading}
          placeholder="Ej: 21001"
          editMode={editMode}
        />
      </Col>

      <Col span={7} offset={1}>
        {hasMultipleCities ? (
          <Form.Item label="Localidad">
            {getFieldDecorator("city", {
              rules: [{ required: true, message: "Campo obligatorio" }],
            })(
              <Select
                showSearch={false}
                placeholder={
                  isLoading ? "Cargando..." : "Seleccione una localidad"
                }
                onChange={(value, option) => handleCitySelect(option.key)}
                disabled={isLoading}
              >
                {cityChoices.map(({ city, cityCode }) => (
                  <Option key={cityCode} value={city}>
                    {city}
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>
        ) : (
          <TextInput
            label="Localidad"
            name="city"
            getFieldDecorator={getFieldDecorator}
            initialValue={initialFormValues?.city ?? null}
            required
            disabled
            requiredMessage="Campo obligatorio"
            placeholder={
              isLoading
                ? "Cargando..."
                : "Campo no editable, introduzca código postal"
            }
          />
        )}
      </Col>

      <Col span={7} offset={1}>
        <TextInput
          label="País"
          name="country"
          getFieldDecorator={getFieldDecorator}
          initialValue={initialFormValues?.country}
          required
          disabled
          requiredMessage="Campo obligatorio"
          placeholder={
            isLoading
              ? "Cargando..."
              : "Campo no editable, introduzca código postal"
          }
        />
      </Col>

      <Col span={7} offset={1} className="hidden-column">
        <TextInput
          label="cityCode"
          name="cityCode"
          getFieldDecorator={getFieldDecorator}
          initialValue={initialFormValues?.cityCode}
          disabled
        />
      </Col>
    </Row>
  );
}
